import type { TicketDocument, TicketLine } from "../../core/platform/receipt/compose-ticket";
import { initPrinter, boldOn, boldOff, text, textLine, lineFeed, feedAndCut } from "./escpos";

/**
 * Ticket layout (T004, FR-206, FR-207, FR-208).
 *
 * CONFIGURED WIDTH, NOT A LITERAL (FR-207). `charsPerLine` is a parameter with
 * a default, not a hardcoded 32 buried in the wrapping logic, so an 80mm roll
 * (48 chars) is a settings change rather than a rewrite.
 *
 * QUANTITY IS UNMISSABLE, NOT JUST PRESENT (FR-206). A "6" printed the same
 * weight as everything else around it is exactly how a six-drink order gets
 * misread as one — the quantity prefix is bolded whenever it's more than one.
 */

export interface TicketLayoutOptions {
  /** Characters per printable line. Default matches 58mm paper (research.md R3). */
  charsPerLine?: number;
  /** Capability-detected by the caller — never assumed. */
  canCut?: boolean;
}

const DEFAULT_CHARS_PER_LINE = 32;
const CONTINUATION_INDENT = "  ";
const FOOTER_FEED_LINES = 4;

function concat(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

/**
 * Word-wrap at a width, never breaking mid-word except for a single word
 * longer than the whole line (FR-208) — the only case where "don't break
 * mid-word" and "don't overflow the page" can't both hold.
 */
export function wrapText(value: string, width: number): string[] {
  if (width <= 0) return [value];

  const words = value.split(/\s+/).filter((word) => word.length > 0);
  if (words.length === 0) return [""];

  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    if (word.length > width) {
      if (current) {
        lines.push(current);
        current = "";
      }
      let remaining = word;
      while (remaining.length > width) {
        lines.push(remaining.slice(0, width));
        remaining = remaining.slice(width);
      }
      current = remaining;
      continue;
    }

    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > width) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function separator(charsPerLine: number): Uint8Array {
  return textLine("-".repeat(charsPerLine));
}

function renderLine(line: TicketLine, charsPerLine: number): Uint8Array[] {
  const out: Uint8Array[] = [];

  const prefix = line.quantity > 1 ? `${line.quantity}x ` : "";
  const firstLineWidth = Math.max(charsPerLine - prefix.length, 1);
  const nameLines = wrapText(line.name, firstLineWidth);

  // First line carries the bold quantity prefix; wrapped continuations do
  // not repeat it — the item is already established.
  if (prefix) {
    out.push(boldOn(), text(prefix), boldOff(), textLine(nameLines[0] ?? ""));
  } else {
    out.push(textLine(nameLines[0] ?? ""));
  }

  const continuationWidth = Math.max(charsPerLine - CONTINUATION_INDENT.length, 1);
  for (const continued of nameLines.slice(1)) {
    for (const wrapped of wrapText(continued, continuationWidth)) {
      out.push(textLine(`${CONTINUATION_INDENT}${wrapped}`));
    }
  }

  if (line.variant) {
    for (const wrapped of wrapText(line.variant, continuationWidth)) {
      out.push(textLine(`${CONTINUATION_INDENT}${wrapped}`));
    }
  }

  for (const modifier of line.modifiers) {
    for (const wrapped of wrapText(`- ${modifier}`, continuationWidth)) {
      out.push(textLine(`${CONTINUATION_INDENT}${wrapped}`));
    }
  }

  if (line.note) {
    for (const wrapped of wrapText(`note: ${line.note}`, continuationWidth)) {
      out.push(textLine(`${CONTINUATION_INDENT}${wrapped}`));
    }
  }

  return out;
}

/**
 * Render a composed ticket into the full ESC/POS byte sequence, ready for the
 * transport. No prices anywhere on this path (FR-205) — `TicketDocument`
 * carries none to begin with.
 */
export function renderTicket(document: TicketDocument, options: TicketLayoutOptions = {}): Uint8Array {
  const charsPerLine = options.charsPerLine ?? DEFAULT_CHARS_PER_LINE;
  const canCut = options.canCut ?? false;

  const chunks: Uint8Array[] = [initPrinter()];

  chunks.push(boldOn(), textLine(`ORDER ${document.orderNumber}`), boldOff());
  chunks.push(textLine(document.issuedAt));
  chunks.push(textLine(`Staff: ${document.cashier}`));
  for (const wrapped of wrapText(document.orderType, charsPerLine)) {
    chunks.push(textLine(wrapped));
  }
  if (document.customerName) {
    for (const wrapped of wrapText(document.customerName, charsPerLine)) {
      chunks.push(textLine(wrapped));
    }
  }
  chunks.push(separator(charsPerLine));

  for (const line of document.lines) {
    chunks.push(...renderLine(line, charsPerLine));
  }

  chunks.push(lineFeed());
  chunks.push(feedAndCut(FOOTER_FEED_LINES, canCut));

  return concat(chunks);
}
